import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers } from 'lucide-react';
import { getSeriesOgImage } from '@/lib/seriesOgImage';

/**
 * Series card (#617). Mirrors the BlogCard "related" variant (same card shell,
 * 1200x630 image area, body padding, CTA colours) so a series reads like a
 * related post. One link only: the whole card is the target, so assistive tech
 * announces it once.
 *
 * `compact` drops the description and CTA for use inside the post's
 * "Part N of M" box, where the surrounding box already carries that text.
 */
const SeriesCard = ({ series, compact = false, className = '', headingLevel: Heading = 'h3' }) => {
  const parts = series.totalParts ?? series.parts.length;
  const href = `/series/${series.slug}`;

  return (
    <article
      className={`relative bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-[#422680] hover:shadow-xl transition-all group flex flex-col h-full focus-within:ring-2 focus-within:ring-[#422680] ${className}`}
    >
      <div className={`relative ${compact ? 'h-40' : 'h-48'} overflow-hidden bg-gradient-to-br from-[#280659] to-[#422680]`}>
        <img
          src={getSeriesOgImage(series.slug)}
          alt={`${series.title}: ${parts}-part series`}
          loading="lazy"
          width={1200}
          height={630}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#422680] text-[10px] md:text-xs font-bold px-2 py-1 md:px-3 rounded-full shadow-sm z-20">
          <Layers size={12} aria-hidden="true" />
          {parts}-part series
        </div>
      </div>

      <div className={`${compact ? 'p-4' : 'p-6'} flex-1 flex flex-col`}>
        <Heading className="text-base md:text-lg font-bold text-gray-900 mb-2">
          {/* Stretched link: the single link covers the whole card. */}
          <Link
            to={href}
            className="after:absolute after:inset-0 hover:text-[#422680] focus:outline-none"
          >
            {series.shortTitle}
          </Link>
        </Heading>
        {!compact && (
          <>
            <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">{series.description}</p>
            <span className="mt-auto inline-flex items-center text-sm md:text-base text-[#422680] group-hover:text-[#F54952]" aria-hidden="true">
              View the series
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </span>
          </>
        )}
      </div>
    </article>
  );
};

export default SeriesCard;
